import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { CartPageContext } from "@insite/content-library/Pages/CartPage";
import CartSaveOrderButton from "@insite/content-library/Widgets/Cart/CartSaveOrderButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import DownloadIcon from "@insite/mobius/Icons/Download";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import ShareCartModal from "@MasterBluePrint/Components/ShareCartModal";
import ShoppingCart from "@MasterBluePrint/Icons/ShoppingCart";
import CartLineCardCondensed, {
    CartLineCardCondensedStyles,
} from "@MasterBluePrint/Overrides/Widgets/Cart/CartLineCardCondensed";
import CartLineCardExpanded, {
    CartLineCardExpandedStyles,
} from "@MasterBluePrint/Overrides/Widgets/Cart/CartLineCardExpanded";
import { getCustomGeneralSettings, getLaunchDarklySettings } from "@MasterBluePrint/Store/Context/ContextSelector";
import sharedCartTracking from "@MasterBluePrint/Tracking/ShareCart/sharedCartTracking";
import DownloadCartLineModal from "@MasterBluePrint/Widgets/Cart/DownloadCartLineModal";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showLineNotes = "showLineNotes",
    errorMessage = "errorMessage",
    showShippingRestrictionIcon = "showShippingRestrictionIcon",
    showHazardousGoodIcon = "showHazardousGoodIcon",
    showCalifPropIcon = "showCalifPropIcon",
    showRoHsIcon = "showRoHsIcon",
    showNcNrIcon = "showNcNrIcon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showLineNotes]: boolean;
        [fields.errorMessage]: string;
        [fields.showShippingRestrictionIcon]: boolean;
        [fields.showHazardousGoodIcon]: boolean;
        [fields.showCalifPropIcon]: boolean;
        [fields.showRoHsIcon]: boolean;
        [fields.showNcNrIcon]: boolean;
    };
}

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    secondaryOutlineButton?: ButtonPresentationProps;
    downloadLinkButton?: ButtonPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isRemovingCartLine: Boolean(
        state.pages.cart.isRemovingCartLine && Object.values(state.pages.cart.isRemovingCartLine).some(v => v),
    ),
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
    homeLink: getPageLinkByPageType(state, "HomePage"),
    launchDarklySettings: getLaunchDarklySettings(state),
    customGeneralSettings: getCustomGeneralSettings(state),
    state,
});

const mapDispatchToProps = {
    clearCurrentCart,
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CartLinesStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    headerWrapper?: InjectableCss;
    header?: CartLinesHeaderStyles;
    cardList?: CardListStyles;
    cardContainer?: CardContainerStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    footerWrapper?: InjectableCss;
    backToTopButton?: ButtonPresentationProps;
    continueShoppingLink?: InjectableCss;
    disApprovingWrapper?: InjectableCss;
    disApprovingText?: InjectableCss;
    saveOrderButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    shareButton?: ButtonPresentationProps;
    addAllTolistButton?: ButtonPresentationProps;
    continueButton?: ButtonPresentationProps;
    confirmRemoveAllModal?: ModalPresentationProps;
    confirmContainer?: InjectableCss;
    confirmMessage?: InjectableCss;
    confirmButtons?: InjectableCss;
    cancelButton?: InjectableCss;
    closeContainer?: InjectableCss;
    removeButton?: ButtonPresentationProps;
    downloadLinkButton?: ButtonPresentationProps;
}

interface CartLinesHeaderStyles {
    overflowMenu?: OverflowMenuPresentationProps;
    removeAllLink?: LinkPresentationProps;
    mainHeadingSectionFirst?: InjectableCss;
    mainHeadingSectionLast?: InjectableCss;
    warningIcon?: IconPresentationProps;
    warningSection?: InjectableCss;
    warningText?: InjectableCss;
}

export const cartLinesStyles: CartLinesStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 18.75rem;
            justify-content: center;
            align-items: center;
            position: relative;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 1.25rem;
        `,
    },
    headerWrapper: {
        css: css`
            padding-bottom: 0.625rem;
            display: flex;
            justify-content: space-between;

            .seperator {
                border-left: 1px solid #c2d1d9;
                height: 0.875rem;
                margin: 0 0.625rem;
            }

            @media print {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            > div:first-child {
                                padding: 0 0.31250625rem 0 7px !important;
                            }
                            .seperator {
                                margin: 0 0.31250625rem;
                            }
                        `,
                        css`
                            > div:first-child {
                                display: flex;
                                padding: 0 0.9375rem 0 0;
                                justify-content: space-between;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    header: {
        overflowMenu: {
            cssOverrides: {
                wrapper: css`
                    color: #002554;
                    font-family: Lato;
                    font-size: 0.875rem;
                    letter-spacing: 0.16px;
                    line-height: 1.125rem;
                    float: right;
                    height: auto;
                    margin-left: 0.625rem;

                    span {
                        color: #002554;
                        display: inline-block;
                        vertical-align: middle;
                        margin-left: 0;

                        span:last-child {
                            color: #002554;
                            margin-left: 0.31250625rem;
                        }
                    }

                    svg {
                        display: inline-block;
                        vertical-align: middle;
                        font-size: 0.875rem;
                        color: #002554;
                        width: 1.25rem;
                        height: 1.25rem;
                    }

                    &:hover,
                    &:active,
                    &:focus {
                        color: #002554 !important;
                        border: none !important;
                        background: none !important;
                        padding: 0;
                    }
                `,
            },
        },
        removeAllLink: {
            css: css`
                margin-left: 1.5rem;
                color: #002554;
                font-family: Lato;
                font-size: 0.875rem;
                letter-spacing: 0.16px;
                line-height: 1.125rem;
                float: right;
                height: auto;
                margin-left: 0.625rem;

                @media screen and (max-width: 767px) {
                    margin: 0;
                }

                @media screen and (min-width: 991px) {
                    margin-left: 2rem;
                }

                @media screen and (min-width: 1100px) {
                    margin-left: 2.7rem;
                }

                @media screen and (min-width: 1200px) {
                    margin-left: 3.5rem;
                }

                span {
                    color: #002554;
                    display: inline-block;
                    vertical-align: middle;
                    margin-left: 0;

                    span:last-child {
                        color: #002554;
                        margin-left: 0.31250625rem;
                    }
                }

                svg {
                    display: inline-block;
                    vertical-align: middle;
                    font-size: 0.875rem;
                    color: #002554;
                    width: 1.25rem;
                    height: 1.25rem;
                }

                &:hover,
                &:active,
                &:focus {
                    color: #002554 !important;
                    border: none !important;
                    background: none !important;
                    padding: 0;
                }
            `,
        },
        mainHeadingSectionFirst: {
            css: css`
                display: flex;
                align-items: center;
                > div > nav > button {
                    background: none;
                    border: none;
                }
            `,
        },
        mainHeadingSectionLast: {
            css: css`
                display: flex;
                align-items: center;
                > div > nav > button {
                    background: none;
                    border: none;
                }

                button[data-test-selector="cart_saveOrder"] {
                    color: #002554 !important;

                    svg {
                        color: #002554 !important;
                    }

                    &:hover,
                    &:active,
                    &:focus {
                        color: #002554 !important;
                        border: none !important;
                        background: none !important;
                        padding: 0;
                    }
                }

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                &.only-desktop {
                                    display: none;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
        warningIcon: {
            src: "AlertCircle",
            color: "danger.main",
            css: css`
                margin-right: 0.625rem;
            `,
        },
        warningSection: {
            css: css`
                display: flex;
                align-items: center;
                border: 2px solid ${getColor("danger.main")};
                padding: 0.9375rem 0.625rem;
                margin-bottom: 0.9375rem;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                margin-bottom: 0;
                                margin-top: 1.5625rem;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    footerWrapper: {
        css: css`
            justify-content: center;
            padding-top: 0.625rem;

            @media print {
                display: none;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .cart-header {
                                display: none;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    backToTopButton: {
        variant: "secondary",
        css: css`
            border-color: ${getColor("common.primary")};
            margin: 1rem 0;
        `,
    },
    continueShoppingLink: {
        css: css`
            padding-bottom: 190px;
            position: relative;
            width: 100%;
            @media screen and (max-width: 990px) {
                padding-bottom: 70px;
            }
            &:after {
                content: "";
                border-bottom: 1px solid #c2d1d9;
                width: 80%;
                position: absolute;
                left: 40%;
                margin-left: -30%;
                top: 92px;

                @media screen and (max-width: 990px) {
                    top: 48px;
                }

                @media screen and (max-width: 767px) {
                    width: 90%;
                    left: 45%;
                    margin-left: -39%;
                }
            }

            @media print {
                padding-bottom: 0;

                &:after {
                    content: none;
                }
            }

            .btn-containter {
                background: #f7f9fa;
                position: absolute;
                top: 74px;
                left: 50%;
                width: 444px;
                margin-left: -222px;
                text-align: center;
                z-index: 1;

                @media screen and (min-width: 768px) and (max-width: 990px) {
                    width: 360px;
                    margin-left: -180px;
                    top: 1.875rem;
                }

                @media screen and (max-width: 767px) {
                    width: 276px;
                    margin-left: -136px;
                    top: 1.875rem;
                }
            }
        `,
    },
    disApprovingWrapper: {
        css: css`
            text-align: center;
            padding: 0.625rem;
            background-color: white;
            border: 2px solid red;
            margin-bottom: 0.9375rem;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin-bottom: 0;
                            margin-top: 1.5rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    disApprovingText: {
        css: css`
            width: 100%;
            color: red;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 0.875rem;
            letter-spacing: 0.16px;
            line-height: 1.125rem;
            float: right;
            height: auto;
            margin-left: 0.625rem;

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 0.31250625rem;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 0.875rem;
                color: #002554;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 0.875rem;
            letter-spacing: 0.16px;
            line-height: 1.125rem;
            float: right;
            height: auto;
            margin-left: 0.625rem;
            margin-right: 0.9375rem;

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 0.31250625rem;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 0.875rem;
                color: #002554;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    shareButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 0.875rem;
            letter-spacing: 0.16px;
            line-height: 1.125rem;
            height: auto;
            margin: 0 0.625rem;

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 0.31250625rem;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 0.875rem;
                color: #002554;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }

            &:disabled {
                color: #ccc !important;
            }
        `,
    },
    addAllTolistButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 0.875rem;
            letter-spacing: 0.16px;
            line-height: 1.125rem;
            height: auto;
            margin: 0 0.625rem;

            @media screen and (max-width: 767px) {
                margin: 0;
            }

            @media screen and (min-width: 991px) {
                margin-left: 2rem;
            }

            @media screen and (min-width: 1100px) {
                margin-left: 2.7rem;
            }

            @media screen and (min-width: 1200px) {
                margin-left: 3.5rem;
            }

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 0.31250625rem;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 0.875rem;
                color: #002554;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
    continueButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border-width: 1px;
            font-family: Roboto;
            font-size: 0.875rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-transform: uppercase;
            width: 344px;
            background: #fff;

            span {
                font-size: 0.875rem;
                font-weight: normal;
            }

            @media screen and (min-width: 768px) and (max-width: 990px) {
                width: 300px;
            }

            @media (max-width: 768px) {
                width: 240px;
            }
        `,
    },
    confirmButtons: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            padding-top: 1.625rem;
            margin-top: 1.875rem;
            border-top: 1px solid #d7d7d7;
        `,
    },
    confirmMessage: {
        css: css`
            h1 {
                color: #002554;
                font-family: "Roboto", sans-serif;
                font-size: 1.25rem;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 32px;
                position: relative;
                margin-bottom: 2.1875rem;
                margin-top: 0.5rem;

                &:after {
                    height: 2px;
                    width: 44px;
                    background-color: #c63527;
                    content: "";
                    display: inline-block;
                    position: absolute;
                    bottom: -12px;
                    left: 1px;
                }
            }

            .icon-alert {
                svg {
                    transform: scale(1.5);
                    stroke: #c63527;
                }
            }

            p {
                color: #002554;
                font-family: Lato, sans-serif;
                font-size: 0.9375rem;
                letter-spacing: 0;
                line-height: 1.3125rem;
            }
        `,
    },
    removeButton: {
        buttonType: "solid",
        css: css`
            border-radius: 2px;
            background-color: #002554;
            border: 2px solid #002554;
            color: #fff;
            text-transform: uppercase;

            &:hover,
            &:active {
                background-color: #002554;
                border: 2px solid #002554;
            }
            span {
                color: #fff;
            }
        `,
    },
    cancelButton: {
        css: css`
            color: #002554;
            font-family: Roboto;
            font-size: 0.9375rem;
            letter-spacing: 0;
            line-height: 1.3125rem;
            text-align: center;
            text-decoration: underline;
            cursor: pointer;
            margin-left: 3em;
            margin-right: 3em;
            justify-content: center;
            width: auto;
        `,
    },
    confirmRemoveAllModal: {
        sizeVariant: "small",
    },
    closeContainer: {
        css: css`
            position: absolute;
            right: 1.25rem;
            top: 24px;
            .close-label {
                color: #535e64;
                font-family: Roboto;
                font-size: 0.875rem;
                letter-spacing: 0.16px;
                line-height: 1;
                display: inline-block;
                margin-right: 0.625rem;
                vertical-align: middle;
            }
            .icon-close {
                display: inline-block;
                cursor: pointer;
                vertical-align: middle;
                line-height: 1;
                width: 1.125rem;
                height: 1.125rem;
                svg {
                    height: 1.125rem;
                    width: 1.125rem;
                }
            }
        `,
    },
    downloadLinkButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            border: none;
            background: none;
            padding: 0;
            color: #002554;
            font-family: Lato;
            font-size: 0.875rem;
            letter-spacing: 0.16px;
            line-height: 1.125rem;
            float: right;
            height: auto;
            margin-left: 0.625rem;
            margin-right: 0.9375rem;

            span {
                display: inline-block;
                vertical-align: middle;

                &:last-child {
                    margin-left: 0.31250625rem;
                }
            }

            svg {
                display: inline-block;
                vertical-align: middle;
                font-size: 0.875rem;
                color: #002554;
                width: 1.25rem;
                height: 1.25rem;
            }

            &:hover,
            &:active,
            &:focus {
                color: #002554 !important;
                border: none !important;
                background: none !important;
                padding: 0;
            }
        `,
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesStyles;

const CartLines: FC<Props> = ({
    cart,
    promotionsDataView,
    settingsCollection,
    clearCurrentCart,
    fields,
    isClearingCart,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    updateCartLine,
    removeCartLine,
    history,
    homeLink,
    launchDarklySettings,
    customGeneralSettings,
    state,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);
    const cartContainsZeroPriceItem = cart?.cartLines?.map(cartLine => cartLine.pricing?.actualPrice).includes(0);
    const allowZeroPricingFound = cart?.cartLines?.some(element => element.allowZeroPricing === true);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    if (!cart || (cart.lineCount && !cart.cartLines) || isClearingCart) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const cartLines = cart.cartLines || [];
    const continueShoppingLabel = translate("Continue Shopping");
    const continueShoppingClickHandler = () => {
        if (homeLink) {
            history.push(homeLink.url);
        }
    };
    if (cartLines.length === 0) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <Icon {...styles.noCartLinesIcon} />
                <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                    {siteMessage("Cart_NoOrderLines")}
                </Typography>
                <Typography as="div" {...styles.continueShoppingLink}>
                    <Typography as="div" className="btn-containter">
                        <Button
                            onClick={continueShoppingClickHandler}
                            {...styles.continueButton}
                            className="cart-btn-continue"
                        >
                            {continueShoppingLabel}
                        </Button>
                    </Typography>
                </Typography>
            </StyledSection>
        );
    }

    const removeAllClickHandler = () => {
        clearCurrentCart();
    };

    const onDownloadClick = () => {
        setIsDownloadModalOpen(true);
    };

    const closeDownloadModal = () => {
        setIsDownloadModalOpen(false);
    };

    const { productSettings } = settingsCollection;
    const { value: promotions } = promotionsDataView;
    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive);
    const isLoadingCart = isLoading[cart.id];
    const isHideShareCartLinkForQuote = customGeneralSettings.hideShareLinkForQuote;

    const cartLinesDisplay = cartLines.map(cartLine => {
        const matchingPromotions = promotions ? promotions.filter(promo => promo.orderLineId === cartLine.id) : [];
        const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
        return (
            <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                {isCondensed ? (
                    <CartLineCardCondensed
                        cart={cart}
                        promotions={matchingPromotions}
                        productSettings={productSettings}
                        showInventoryAvailability={true}
                        extendedStyles={styles.cardCondensed}
                        updateCartLine={updateCartLine}
                        removeCartLine={removeCartLine}
                        showRemoveAction={showRemoveAction}
                    />
                ) : (
                    <CartLineCardExpanded
                        cart={cart}
                        promotions={matchingPromotions}
                        productSettings={productSettings}
                        showInventoryAvailability={true}
                        showLineNotes={fields.showLineNotes}
                        extendedStyles={styles.cardExpanded}
                        updateCartLine={updateCartLine}
                        removeCartLine={removeCartLine}
                        showRemoveAction={showRemoveAction}
                        showHazardousGoodIcon={fields.showHazardousGoodIcon}
                        showShippingRestrictionIcon={fields.showShippingRestrictionIcon}
                        showCalifPropIcon={fields.showCalifPropIcon}
                        showRoHsIcon={fields.showRoHsIcon}
                        showNcNrIcon={fields.showNcNrIcon}
                    />
                )}
            </CartLineContext.Provider>
        );
    });

    return (
        <StyledSection {...styles.wrapper}>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isLoadingCart || isUpdatingCartLine || isRemovingCartLine || promotionsDataView.isLoading}
            >
                <CartLinesHeader
                    totalItemCount={cartLines!.length}
                    isCondensed={isCondensed}
                    productsCannotBePurchased={productsCannotBePurchased}
                    cartNotPriced={cart.cartNotPriced}
                    failedToGetRealTimeInventory={cart.failedToGetRealTimeInventory}
                    onRemoveAllClick={removeAllClickHandler}
                    cartContainsZeroPriceItem={cartContainsZeroPriceItem}
                    errorMessage={fields.errorMessage}
                    cart={cart}
                    state={state}
                    allowZeroPricingFound={allowZeroPricingFound}
                    isHideShareCartLinkForQuote={isHideShareCartLinkForQuote}
                    onDownloadClick={onDownloadClick}
                />
                {cartLinesDisplay}
                <CartLinesFooter
                    onRemoveAllClick={removeAllClickHandler}
                    cart={cart}
                    history={history}
                    homeLink={homeLink}
                    launchDarklySettings={launchDarklySettings}
                />
            </LoadingOverlay>
            <DownloadCartLineModal isOpen={isDownloadModalOpen} onClose={closeDownloadModal} cart={cart} />
        </StyledSection>
    );
};

interface CartLinesHeaderProps {
    totalItemCount: number;
    isCondensed: boolean;
    productsCannotBePurchased?: boolean;
    cartNotPriced?: boolean;
    failedToGetRealTimeInventory?: boolean;
    onRemoveAllClick: () => void;
    cartContainsZeroPriceItem: boolean | undefined;
    errorMessage: string;
    cart: any;
    state: any;
    allowZeroPricingFound: boolean | undefined;
    isHideShareCartLinkForQuote: boolean | undefined;
    onDownloadClick: () => void;
}
const CartLinesHeader: FC<CartLinesHeaderProps> = ({
    totalItemCount,
    isCondensed,
    productsCannotBePurchased,
    cartNotPriced,
    failedToGetRealTimeInventory,
    onRemoveAllClick,
    cartContainsZeroPriceItem,
    errorMessage,
    cart,
    state,
    allowZeroPricingFound,
    isHideShareCartLinkForQuote,
    onDownloadClick,
}) => {
    const headerStyles = styles.header || {};
    const defaultErrorMessage =
        "One or more items in your cart cannot be purchased in the online store. Please contact sales at (800) 950-3457";
    const [isOpen, setisOpen] = React.useState(false);
    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    useEffect(() => {
        if (cart.properties?.isquote === "true") {
            setIsQuoteCart(true);
        }
    }, [cart]);
    const shareClickHandler = () => {
        sharedCartTracking(state, cart);
        setisOpen(true);
    };
    const closeMiniCart = () => {
        setisOpen(false);
    };
    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };
    const [isconfirmRemoveAll, setIsconfirmRemoveAll] = useState(false);

    const confirmRemoveAll = () => {
        setIsconfirmRemoveAll(true);
    };

    const confirmRemoveAllClose = () => {
        setIsconfirmRemoveAll(false);
    };
    return (
        <>
            {productsCannotBePurchased && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_ProductsCannotBePurchased")}
                    </Typography>
                </StyledSection>
            )}
            {cartNotPriced && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_NoPriceAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {failedToGetRealTimeInventory && (
                <StyledSection {...headerStyles.warningSection}>
                    <Icon {...headerStyles.warningIcon}></Icon>
                    <Typography {...headerStyles.warningText}>
                        {siteMessage("Cart_NoInventoryAvailableAtCheckout")}
                    </Typography>
                </StyledSection>
            )}
            {cartContainsZeroPriceItem && !allowZeroPricingFound && (
                <StyledWrapper {...styles.disApprovingWrapper}>
                    <Typography {...styles.disApprovingText}>
                        {translate(errorMessage || defaultErrorMessage)}
                    </Typography>
                </StyledWrapper>
            )}

            <StyledSection {...styles.headerWrapper} id="cartHeader" className="cart-header">
                <Typography {...headerStyles.mainHeadingSectionFirst} as="div">
                    <Link
                        onClick={confirmRemoveAll}
                        {...headerStyles.removeAllLink}
                        data-test-selector="cartlineHeader_removeAll"
                    >
                        <Icon src="Trash2" />
                        <span>{translate("Remove All Products")}</span>
                    </Link>
                    <Modal
                        isOpen={isconfirmRemoveAll}
                        data-test-selector="confirmRemoveAllModal"
                        {...styles.confirmRemoveAllModal}
                        handleClose={() => confirmRemoveAllClose()}
                        className="confirm-remove-modal"
                    >
                        <Typography as="div" {...styles.confirmContainer}>
                            <Typography as="div" onClick={confirmRemoveAllClose} {...styles.closeContainer}>
                                <span className="close-label">Close</span>
                                <Icon className="icon-close" src="X" />
                            </Typography>
                            <Typography as="div" {...styles.confirmMessage}>
                                <Icon className="icon-alert" src="AlertCircle" />
                                <h1>Remove all products?</h1>
                                <p>Do you want to remove all products from your shopping cart?</p>
                            </Typography>
                            <Typography as="div" {...styles.confirmButtons}>
                                <Clickable onClick={confirmRemoveAllClose} {...styles.cancelButton}>
                                    {translate("Cancel")}
                                </Clickable>
                                <Button onClick={onRemoveAllClick} {...styles.removeButton}>
                                    {translate("Remove All Products")}
                                </Button>
                            </Typography>
                        </Typography>
                    </Modal>
                </Typography>
                <Typography as="div" {...headerStyles.mainHeadingSectionLast}>
                    {cart?.cartLines &&
                        cart?.cartLines?.length > 0 &&
                        (!isHideShareCartLinkForQuote || !isQuoteCart) && (
                            <Button
                                {...styles.shareButton}
                                onClick={shareClickHandler}
                                data-test-selector="shareList"
                                disabled={isQuoteCart}
                            >
                                <Icon src="Share2" />
                                <span>{translate("Share this Cart")}</span>
                            </Button>
                        )}
                    <ShareCartModal isOpen={isOpen} closeShareCart={closeMiniCart}></ShareCartModal>
                    <Hidden below="lg">
                        {(!isHideShareCartLinkForQuote || !isQuoteCart) && <span className="seperator" />}
                        <Button {...styles.printButton} onClick={printOrOpenPrintAllModal}>
                            <Icon src="Printer" />
                            <span>{translate("Print")}</span>
                        </Button>
                    </Hidden>
                    <span className="seperator" />
                    <Button
                        onClick={onDownloadClick}
                        variant="secondary"
                        {...styles.downloadLinkButton}
                        data-test-selector="cart_download"
                    >
                        <Icon src={DownloadIcon} color="currentColor" />
                        <span>{translate("Download Cart")}</span>
                    </Button>
                </Typography>
            </StyledSection>
        </>
    );
};

interface CartLinesFooterProps {
    onRemoveAllClick: () => void;
    cart: any;
    history: any;
    homeLink: any;
    launchDarklySettings: any;
}

const CartLinesFooter: FC<CartLinesFooterProps> = ({ onRemoveAllClick, cart, history, homeLink }) => {
    const headerStyles = styles.header || {};
    const [isQuoteCart, setIsQuoteCart] = React.useState(false);
    useEffect(() => {
        if (cart.properties?.isquote === "true") {
            setIsQuoteCart(true);
        }
    }, [cart]);
    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const [isconfirmRemoveAll, setIsconfirmRemoveAll] = useState(false);

    const confirmRemoveAll = () => {
        setIsconfirmRemoveAll(true);
    };

    const confirmRemoveAllClose = () => {
        setIsconfirmRemoveAll(false);
    };

    const continueShoppingLabel = translate("Continue Shopping");
    const continueShoppingClickHandler = () => {
        if (homeLink) {
            history.push(homeLink.url);
        }
    };

    const featureFlags = ["TEUS-7937"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const isContinueButtonOnCartPageHidden = flagValues["TEUS-7937"];

    return (
        <StyledSection {...styles.footerWrapper} className="container-footer">
            {cart?.cartLines?.length > 3 && (
                <Typography {...styles.headerWrapper} id="cartHeader" className="cart-header">
                    <Typography {...headerStyles.mainHeadingSectionFirst} as="div">
                        <Link
                            onClick={confirmRemoveAll}
                            {...headerStyles.removeAllLink}
                            data-test-selector="cartlineHeader_removeAll"
                        >
                            <Icon src="Trash2" />
                            <span>{translate("Remove All Products")}</span>
                        </Link>
                        <Modal
                            isOpen={isconfirmRemoveAll}
                            data-test-selector="confirmRemoveAllModal"
                            {...styles.confirmRemoveAllModal}
                            handleClose={() => confirmRemoveAllClose()}
                            className="confirm-remove-modal"
                        >
                            <Typography as="div" {...styles.confirmContainer}>
                                <Typography as="div" onClick={confirmRemoveAllClose} {...styles.closeContainer}>
                                    <span className="close-label">Close</span>
                                    <Icon className="icon-close" src="X" />
                                </Typography>
                                <Typography as="div" {...styles.confirmMessage}>
                                    <Icon className="icon-alert" src="AlertCircle" />
                                    <h1>Remove all products?</h1>
                                    <p>Do you want to remove all products from your shopping cart?</p>
                                </Typography>
                                <Typography as="div" {...styles.confirmButtons}>
                                    <Clickable onClick={confirmRemoveAllClose} {...styles.cancelButton}>
                                        {translate("Cancel")}
                                    </Clickable>
                                    <Button onClick={onRemoveAllClick} {...styles.removeButton}>
                                        {translate("Remove All Products")}
                                    </Button>
                                </Typography>
                            </Typography>
                        </Modal>
                    </Typography>
                    <Typography as="div" {...headerStyles.mainHeadingSectionLast} className="only-desktop">
                        <Hidden below="lg">
                            {cart?.cartLines && cart?.cartLines?.length > 0 && (
                                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
                            )}
                        </Hidden>
                        <Hidden below="lg">
                            <span className="seperator" />
                            <Button {...styles.printButton} onClick={printOrOpenPrintAllModal}>
                                <Icon src="Printer" />
                                <span>{translate("Print")}</span>
                            </Button>
                        </Hidden>
                    </Typography>
                </Typography>
            )}
            {!isContinueButtonOnCartPageHidden && (
                <Typography as="div" {...styles.continueShoppingLink}>
                    <Typography as="div" className="btn-containter">
                        <Button
                            onClick={continueShoppingClickHandler}
                            {...styles.continueButton}
                            disabled={isQuoteCart}
                        >
                            {continueShoppingLabel}
                        </Button>
                    </Typography>
                </Typography>
            )}
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartLines)),
    definition: {
        group: "Cart",
        allowedContexts: [CartPageContext],
        fieldDefinitions: [
            {
                name: fields.showLineNotes,
                displayName: "Show Line Notes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                tooltip: "This setting does not apply to Punchout orders, which will never display Line Notes.",
            },
            {
                name: fields.errorMessage,
                displayName: "Error Message",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.showShippingRestrictionIcon,
                displayName: "Show Shipping Restriction Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
            },
            {
                name: fields.showHazardousGoodIcon,
                displayName: "Show Hazardous Good Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
            },
            {
                name: fields.showCalifPropIcon,
                displayName: "Show Calif Prop Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
            },
            {
                name: fields.showRoHsIcon,
                displayName: "Show RoHs Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
            },
            {
                name: fields.showNcNrIcon,
                displayName: "Show NCNR Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
